import React, { useEffect, useRef, useState } from 'react';
import {
  Button,
  Checkbox,
  Icon,
  Input,
  Loader,
  Message,
  Modal,
  Table,
} from 'semantic-ui-react';
import { useTranslation } from 'react-i18next';
import { API, isRoot } from '../../helpers';
import {
  commercialBaseV106,
  unwrapCommercialV106,
  validCommercialFactorV106,
} from '../CommercialV106/shared';
import './OrganizationSourcesV109.css';
import {
  sourceMatchesV1019,
  organizationSourceOverrideV1019,
} from '../CommercialV106/sourceIdentityV1019';

const identity = (row) => `${row.provider_id}:${row.source_id}`;
const defaults = (accountId) => ({
  account_id: Number(accountId),
  price_factor: '1',
  allowed_offers: null,
  model_offers: {},
  allowed_channel_ids: null,
  denied_channel_ids: [],
  minimum_health: 0,
  minimum_samples: 0,
  allow_unobserved: true,
  default_strategy: 'inherit',
  model_price_factors: {},
  stack_monthly_discount: true,
});

export const updateOrganizationSourceV109 = (config, source, patch) => {
  const rows = config.source_overrides_v109 || [];
  const existing = organizationSourceOverrideV1019(config, source);
  const next = {
    provider_id: source.provider_id,
    source_id: source.source_id,
    ...existing,
    ...patch,
  };
  if (!next.price_factor) delete next.price_factor;
  delete next.identity_conflict_v1019;
  const linked = (source.legacy_source_refs_v114 || []).length > 0;
  // Old rows can still govern one-sided models outside this association.
  const remaining = rows.filter((row) => linked
    ? identity(row) !== identity(source)
    : !sourceMatchesV1019(source, row));
  const stable = linked || source.provider_id.startsWith('shared-');
  if (patch === null && stable) {
    remaining.push({ provider_id: source.provider_id, source_id: source.source_id });
  } else if (patch !== null && (next.price_factor || next.disabled || stable))
    remaining.push(next);
  return { ...config, source_overrides_v109: remaining };
};

export function OrganizationSourcesV109({ organization, onClose, onSaved }) {
  const { i18n } = useTranslation();
  const zh = i18n?.language?.startsWith('zh');
  const text = (cn, en) => (zh ? cn : en);
  const accountId = organization?.account?.id;
  const root = isRoot();
  const [state, setState] = useState(null);
  const [config, setConfig] = useState(null);
  const [sources, setSources] = useState([]);
  const [preview, setPreview] = useState(null);
  const [error, setError] = useState('');
  const [busy, setBusy] = useState(false);
  const [query, setQuery] = useState('');
  const [reload, setReload] = useState(0);
  const epoch = useRef(0);
  useEffect(() => {
    const request = ++epoch.current;
    setState(null);
    setConfig(null);
    setPreview(null);
    setError('');
    setBusy(false);
    setQuery('');
    setSources([]);
    if (!accountId || !root) return undefined;
    Promise.all([
      API.get(commercialBaseV106 + '/policy', {
        params: { scope_key: `org/${accountId}` },
      }),
      API.get(commercialBaseV106 + '/organization-sources', {
        params: { account_id: accountId },
      }),
    ])
      .then(([policy, directory]) => {
        if (request !== epoch.current) return;
        const current = unwrapCommercialV106(policy);
        const scoped = unwrapCommercialV106(directory);
        const rows = scoped?.sources;
        if (!Array.isArray(rows) || !Number.isSafeInteger(current?.revision))
          throw new Error(
            text('无法读取 Source 配置', 'Source configuration is unavailable')
          );
        const value = current.config || defaults(accountId);
        if (
          Number(value.account_id) !== Number(accountId) ||
          Number(scoped.account_id) !== Number(accountId)
        )
          throw new Error(
            text('组织身份不匹配', 'Organization identity mismatch')
          );
        setState({ revision: current.revision, config: value });
        setConfig(value);
        setSources(rows);
      })
      .catch((failure) => {
        if (request === epoch.current) setError(failure.message);
      });
    return () => {
      epoch.current += 1;
    };
    // Language changes must not discard an unsaved draft.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [accountId, root, reload]);

  if (!accountId || !root) return null;
  const overrides = config?.source_overrides_v109 || [];
  const visible = sources.filter((row) =>
    [row.name, row.source_id, row.provider_name, ...(row.models || [])]
      .join(' ')
      .toLowerCase()
      .includes(query.toLowerCase())
  );
  const valid = overrides.every(
    (row) => !row.price_factor || validCommercialFactorV106(row.price_factor)
  );
  const dirty =
    config && JSON.stringify(config) !== JSON.stringify(state?.config);
  const update = (source, patch) => {
    setConfig((current) =>
      updateOrganizationSourceV109(current, source, patch)
    );
    setPreview(null);
    setError('');
  };
  const publish = async () => {
    const request = epoch.current;
    setBusy(true);
    setError('');
    try {
      const response = await API.post(
        commercialBaseV106 + (preview ? '/commit' : '/preview'),
        preview
          ? { preview_id: preview.preview_id, confirm: true }
          : {
              scope_key: `org/${accountId}`,
              expected_revision: state.revision,
              config,
            },
        { validateStatus: () => true }
      );
      const data = unwrapCommercialV106(response);
      if (request !== epoch.current) return;
      if (preview) {
        setState({ revision: data.revision, config: data.config });
        setConfig(data.config);
        setPreview(null);
        onSaved?.();
      } else {
        setPreview(data);
      }
    } catch (failure) {
      if (request === epoch.current) setError(failure.message);
    } finally {
      if (request === epoch.current) setBusy(false);
    }
  };
  return (
    <Modal
      open
      onClose={busy ? undefined : onClose}
      closeIcon={!busy}
      closeOnDimmerClick={false}
      closeOnEscape={!busy}
      size='large'
      className='organization-sources-v109'
    >
      <Modal.Header>
        {text(
          '渠道（Source）倍率与可用性',
          'Source multipliers and availability'
        )}{' '}
        · {organization.account.name}
      </Modal.Header>
      <Modal.Content scrolling>
        {error && (
          <Message negative>
            {error}
            <Button
              type='button'
              icon
              basic
              disabled={busy}
              title={text('重新载入', 'Reload')}
              aria-label={text('重新载入', 'Reload')}
              onClick={() => setReload((value) => value + 1)}
            >
              <Icon name='refresh' />
            </Button>
          </Message>
        )}
        {!config ? (
          !error && <Loader active inline='centered' />
        ) : (
          <>
            <div className='organization-sources-toolbar-v109'>
              <Input
                icon='search'
                placeholder={text(
                  '搜索 Source、站点或模型',
                  'Search Source, site or model'
                )}
                aria-label={text('搜索 Source', 'Search Source')}
                value={query}
                onChange={(_, { value }) => setQuery(value)}
              />
              <span>
                {text('组织默认倍率', 'Organization default factor')} ×
                {config.price_factor} · v{state.revision}
              </span>
            </div>
            <div
              className='organization-sources-scroll-v109'
              role='region'
              aria-label={text(
                '组织 Source 配置',
                'Organization Source configuration'
              )}
              tabIndex={0}
            >
              <Table basic='very' unstackable compact>
                <Table.Header>
                  <Table.Row>
                    <Table.HeaderCell>Source</Table.HeaderCell>
                    <Table.HeaderCell>
                      {text('全局倍率', 'Global multiplier')}
                    </Table.HeaderCell>
                    <Table.HeaderCell>
                      {text('组织倍率', 'Organization factor')}
                    </Table.HeaderCell>
                    <Table.HeaderCell>
                      {text('组织可用', 'Organization access')}
                    </Table.HeaderCell>
                    <Table.HeaderCell>{text('重置', 'Reset')}</Table.HeaderCell>
                  </Table.Row>
                </Table.Header>
                <Table.Body>
                  {visible.map((source) => {
                    const key = identity(source);
                    const override = organizationSourceOverrideV1019(config, source);
                    const invalid =
                      override?.price_factor &&
                      !validCommercialFactorV106(override.price_factor);
                    return (
                      <Table.Row key={key} data-source={key}>
                        <Table.Cell>
                          <strong>{source.name}</strong>
                          {override?.identity_conflict_v1019 && (
                            <span role='alert' className='organization-source-invalid-v109'>
                              {text('旧 Source 配置冲突，已阻止使用', 'Conflicting legacy Source rules; access blocked')}
                            </span>
                          )}
                          <span className='organization-source-meta-v109'>
                            {source.provider_name}
                          </span>
                          <span
                            className='organization-source-meta-v109'
                            title={(source.models || []).join(', ')}
                          >
                            {source.missing
                              ? text('Source 已移除', 'Source removed')
                              : `${source.models.length} ${text(
                                  '个模型',
                                  'models'
                                )}`}
                            {!source.enabled && !source.missing
                              ? ` · ${text(
                                  '全局未开放',
                                  'Not globally enabled'
                                )}`
                              : ''}
                          </span>
                        </Table.Cell>
                        <Table.Cell
                          data-label={text('全局倍率', 'Global multiplier')}
                        >
                          {source.dynamic
                            ? text('按实际回执', 'Actual receipt')
                            : source.multipliers
                                .map((value) => `×${value}`)
                                .join(' / ') || '-'}
                        </Table.Cell>
                        <Table.Cell
                          data-label={text('组织倍率', 'Organization factor')}
                        >
                          <Input
                            value={override?.price_factor || ''}
                            inputMode='decimal'
                            disabled={busy}
                            error={Boolean(invalid)}
                            aria-label={`${source.name} ${text(
                              '组织倍率',
                              'organization factor'
                            )}`}
                            aria-invalid={Boolean(invalid)}
                            placeholder={text('继承', 'Inherit')}
                            onChange={(_, { value }) =>
                              update(source, { price_factor: value })
                            }
                          />
                          {invalid && (
                            <span
                              role='alert'
                              className='organization-source-invalid-v109'
                            >
                              {text(
                                '范围 0–100，不含 0，最多 12 位小数',
                                '>0 to 100, up to 12 decimals'
                              )}
                            </span>
                          )}
                        </Table.Cell>
                        <Table.Cell>
                          <Checkbox
                            toggle
                            checked={!override?.disabled}
                            disabled={busy}
                            label={
                              override?.disabled
                                ? text('禁用', 'Denied')
                                : text('允许', 'Allowed')
                            }
                            aria-label={`${source.name} ${text(
                              '组织可用',
                              'organization access'
                            )}`}
                            onChange={(_, { checked }) =>
                              update(source, { disabled: !checked })
                            }
                          />
                        </Table.Cell>
                        <Table.Cell>
                          <Button
                            type='button'
                            icon
                            basic
                            disabled={busy || !override}
                            title={text('恢复继承', 'Restore inheritance')}
                            aria-label={`${source.name} ${text(
                              '恢复继承',
                              'restore inheritance'
                            )}`}
                            onClick={() => update(source, null)}
                          >
                            <Icon name='undo' />
                          </Button>
                        </Table.Cell>
                      </Table.Row>
                    );
                  })}
                </Table.Body>
              </Table>
            </div>
            {visible.length === 0 && (
              <Message
                content={text('没有匹配的 Source', 'No matching Sources')}
              />
            )}
            {preview && (
              <Message
                warning
                role='status'
                content={text(
                  `确认发布 ${organization.account.name} 的 Source 配置，版本 ${
                    state.revision + 1
                  }？`,
                  `Publish Source configuration for ${
                    organization.account.name
                  }, revision ${state.revision + 1}?`
                )}
              />
            )}
          </>
        )}
      </Modal.Content>
      <Modal.Actions>
        <Button type='button' disabled={busy} onClick={onClose}>
          {text('关闭', 'Close')}
        </Button>
        <Button
          type='button'
          primary
          disabled={busy || !config || !valid || !dirty}
          loading={busy}
          onClick={publish}
        >
          <Icon name={preview ? 'check' : 'eye'} />
          {preview
            ? text('确认保存', 'Confirm save')
            : text('预览修改', 'Preview changes')}
        </Button>
      </Modal.Actions>
    </Modal>
  );
}
